import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { HeroChicago } from "@/components/site/HeroChicago";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { ValueProps } from "@/components/site/ValueProps";
import { Footer } from "@/components/site/Footer";
import { WhyChooseDallas } from "@/components/site/WhyChooseDallas";
import kurtWagner from "@/assets/restore/kurt-wagner.webp.asset.json";
import drHarrisScott from "@/assets/restore/dr-harris-scott.webp.asset.json";
import { doctors } from "@/lib/site-data";
import { safeJsonLd } from "@/lib/structured-data";
import { getDallasStructuredData } from "@/lib/structured-data/dallas";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const NoShaveFuture = lazy(() => import("@/components/site/NoShaveFuture").then(m => ({ default: m.NoShaveFuture })));
const NoShaveWhy = lazy(() => import("@/components/site/NoShaveWhy").then(m => ({ default: m.NoShaveWhy })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const DoctorBio = lazy(() => import("@/components/site/DoctorBio").then(m => ({ default: m.DoctorBio })));
const Surgeons = lazy(() => import("@/components/site/Surgeons").then(m => ({ default: m.Surgeons })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then(m => ({ default: m.DoctorsGrid })));
const LocationsMapDallas = lazy(() => import("@/components/site/LocationsMapDallas").then(m => ({ default: m.LocationsMapDallas })));
const VirtualConsult = lazy(() => import("@/components/site/VirtualConsult").then(m => ({ default: m.VirtualConsult })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

const FUE_VIDEO_URL = "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/F.U.E.ExplainedRESTOREHairWhatisFUE.mp4";

export const Route = createFileRoute("/locations/restore-dallas")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/restore-dallas/" }],
    meta: [
      { title: "Get Your Hair Back | RESTORE® Hair Transplants — Dallas" },
      {
        name: "description",
        content:
          "Restore your confidence with the top hair restoration team serving Dallas and the surrounding Texas metro. Over 20,000 procedures.",
      },
      { property: "og:title", content: "Get Your Hair Back | RESTORE® Hair Transplants — Dallas" },
      {
        property: "og:description",
        content:
          "Top hair restoration team trusted by celebrities, athletes & everyday guys. Soft, natural, undetectable results.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getDallasStructuredData() }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  useLocalQuoteLinks();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <HeroChicago
          eyebrow="RESTORE® | Dallas Hair Transplants"
          subheading="RESTORE your confidence with the #1 hair restoration team in Dallas"
        />
        <NorwoodQuiz />
        <WhyChooseDallas />
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <Results />
          </div>
          <ValueProps
            hideEyebrowMobile
            imageSrc={drHarrisScott.url}
            imageAlt="Dr. James Harris with RESTORE client Scott N."
            videoUrl={FUE_VIDEO_URL}
          />
          <NoShaveFuture />
          <NoShaveWhy dougLeft />
          <Reviews />
          <Guarantee />
          <Pricing
            imageSrc={kurtWagner.url}
            imageAlt="Kurt Wagner, RESTORE Hair Client"
            city="Dallas"
            hidePlayOverlay
          />
          <DoctorBio />
          <Surgeons eyebrow="RESTORE Dallas Hair Transplant Doctors" heading={<>A World Renowned <span className="text-foreground">Team</span></>} />
          <DoctorsGrid doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Fahad Juboori"].includes(d.name))} />
          <LocationsMapDallas />
          <Faq />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}